import React, {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useMemo,
} from 'react';

import { useLazyQuery } from '@apollo/client';

import { QUERY_DISCO_CALCULATE_VOLUMES } from 'client/app/apps/disco-prototype/api/queries/discoCalculateVolumes';
import { convertStateToRequest } from 'client/app/apps/disco-prototype/state/request/convert';
import { useStateContext } from 'client/app/apps/disco-prototype/StateProvider';

type CompiledStepsContextType = {
  handleCalculateVolumes: () => void;
};

export const CompiledStepsContext = createContext<CompiledStepsContextType | undefined>(
  undefined,
);

export const useCompiledStepsContext = () => {
  const context = useContext(CompiledStepsContext);
  if (context === undefined) {
    throw new Error(
      'useCompiledStepsContext must be used within a CompiledStepsProvider',
    );
  }
  return context;
};

export function CompiledStepsProvider({ children }: PropsWithChildren) {
  const { steps, tables, liquidSets, handleUpdateResponse } = useStateContext();
  const { request, handleResponse } = useMemo(
    () => convertStateToRequest({ steps, tables, liquidSets }),
    [liquidSets, steps, tables],
  );
  const [calculateVolumes] = useLazyQuery(QUERY_DISCO_CALCULATE_VOLUMES, {
    onCompleted: response => {
      const {
        tables: newTables,
        liquidSets: newLiquidSets,
        steps: newSteps,
      } = handleResponse(response.discoCalculateVolumes);
      handleUpdateResponse(newTables, newLiquidSets, newSteps);
    },
  });

  const handleCalculateVolumes = useCallback(async () => {
    void calculateVolumes({ variables: { request } });
  }, [calculateVolumes, request]);

  const value = {
    handleCalculateVolumes,
  };

  return (
    <CompiledStepsContext.Provider value={value}>
      {children}
    </CompiledStepsContext.Provider>
  );
}
